import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  MenuItem,
  TextField,
  InputAdornment,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkIcon from "@mui/icons-material/Link";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DropdownTextField from "../form-components/DropdownTextField";
import MediaPreviewOverlay from "../common/MediaPreviewOverlay";
import { SectionLabel } from "../portals/PortalShared";
import { emptyPosition, positionIsComplete, positionIsPartial } from "./CustomItemShared";
import LocalText from "../common/LocalText";
import { useLocalText } from "../common/useLocalText";
import { hasRequiredName, isValidUrl } from '../../utils/validators';
import useUrlValidation from '../../hooks/useUrlValidation';
import UrlStatusIcon from '../common/UrlStatusIcon';
import { getCustomBoothItemTypes } from "../../api/customBoothItemApi";

const DEFAULT_LOCALE = "en_us";
const POSITION_LABELS = ["Left", "Center", "Right"];
const emptyValidation = () => ({ linkOk: true, thumbnailOk: true });

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const CustomItemModal = ({
  open, onClose, onSave,
  initialTypeId,
  initialNamePairs,
  initialDescriptionPairs,
  initialImagePairs,
  initialLinks,          // [{ id, positionId, externalLink, thumbnailMedia }]
  initialStartDateTime,
  initialEndDateTime,
  isEditing = false,
  readOnly = false,
  title,
  subtitle,
  saveLabel,
}) => {
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typeId, setTypeId] = useState("");
  const [namePairs, setNamePairs] = useState([{ localeId: DEFAULT_LOCALE, value: "" }]);
  const [descriptionPairs, setDescriptionPairs] = useState([{ localeId: DEFAULT_LOCALE, value: "" }]);
  const [imagePairs, setImagePairs] = useState([{ localeId: DEFAULT_LOCALE, mediaLink: "" }]);
  const [positions, setPositions] = useState(() => Array.from({ length: 3 }, emptyPosition));
  const [activePosition, setActivePosition] = useState(0);
  const [positionValidation, setPositionValidation] = useState(() => Array.from({ length: 3 }, emptyValidation));
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [imageUrlsValid, setImageUrlsValid] = useState(true);
  const [linkPreviewOpen, setLinkPreviewOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    getCustomBoothItemTypes()
      .then((data) => setTypes(Array.isArray(data) ? data : []))
      .catch(() => setTypes([]))
      .finally(() => setTypesLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setActivePosition(0);
    setTypeId(initialTypeId ?? "");
    setNamePairs(initialNamePairs?.length ? initialNamePairs : [{ localeId: DEFAULT_LOCALE, value: "" }]);
    setDescriptionPairs(initialDescriptionPairs?.length ? initialDescriptionPairs : [{ localeId: DEFAULT_LOCALE, value: "" }]);
    setImagePairs(initialImagePairs?.length ? initialImagePairs : [{ localeId: DEFAULT_LOCALE, mediaLink: "" }]);

    const loadedPositions = Array.from({ length: 3 }, (_, i) => {
      const link = initialLinks?.find((l) => l.positionId === i);
      if (!link) return emptyPosition();
      return {
        link: link.externalLink || "",
        thumbnailLocalizations:
          link.thumbnailMedia?.linkLocalizations?.length > 0
            ? link.thumbnailMedia.linkLocalizations.map((l) => ({ localeId: l.localeId, mediaLink: l.mediaLink }))
            : [{ localeId: DEFAULT_LOCALE, mediaLink: "" }],
      };
    });
    setPositions(loadedPositions);
    setPositionValidation(Array.from({ length: 3 }, emptyValidation));

    setStartDateTime(toDatetimeLocal(initialStartDateTime));
    setEndDateTime(toDatetimeLocal(initialEndDateTime));
    setImageUrlsValid(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const selectedType = types.find((t) => t.id === typeId);
  const isCalendar = selectedType?.typeKey === "calendar";

  const position = positions[activePosition] ?? emptyPosition();
  const updatePosition = (field, value) =>
    setPositions((prev) => prev.map((p, i) => (i === activePosition ? { ...p, [field]: value } : p)));

  const { status: linkStatus } = useUrlValidation(position.link, 'url');
  const linkPlaceholder = useLocalText("https://...");

  const activePositionRef = useRef(activePosition);
  activePositionRef.current = activePosition;

  useEffect(() => {
    setPositionValidation((pv) =>
      pv.map((v, i) => (i === activePosition ? { ...v, linkOk: linkStatus === 'valid' || linkStatus === 'idle' } : v))
    );
  }, [linkStatus, activePosition]);

  const handleThumbnailValidation = useCallback((allValid) => {
    setPositionValidation((pv) => pv.map((v, i) => (i === activePositionRef.current ? { ...v, thumbnailOk: allValid } : v)));
  }, []);
  const handleImageValidation = useCallback((allValid) => setImageUrlsValid(allValid), []);

  const allLinksValid = positions.every((pos, i) => {
    const link = (pos?.link ?? '').trim();
    if (!link) return true;
    if (positionValidation[i]?.linkOk === false) return false;
    return isValidUrl(link);
  });

  const allThumbnailsValid = positions.every((pos, i) => {
    if (positionValidation[i]?.thumbnailOk === false) return false;
    return (pos.thumbnailLocalizations ?? []).every((t) => !(t.mediaLink ?? '').trim() || isValidUrl(t.mediaLink));
  });

  const noPartialPositions = !positions.some(positionIsPartial);
  const hasImage = imagePairs.some((p) => (p.mediaLink ?? '').trim());
  const calendarValid = !isCalendar || (startDateTime && endDateTime && new Date(endDateTime) > new Date(startDateTime));

  const isValid =
    !!typeId &&
    hasRequiredName(namePairs) &&
    hasImage &&
    imageUrlsValid &&
    noPartialPositions &&
    allLinksValid &&
    allThumbnailsValid &&
    calendarValid;

  const linkHelperText = readOnly ? '' : (() => {
    if (linkStatus === 'checking') return 'Checking…';
    if (linkStatus === 'invalid') return isValidUrl(position.link) ? 'URL could not be reached' : 'URL must start with http:// or https://';
    if (linkStatus === 'valid') return 'Valid';
    return positionIsPartial(position) ? 'Required — thumbnail already set for this position' : '';
  })();

  const handleSave = () => {
    onSave({
      customBoothItemTypeId: typeId,
      optionType: selectedType?.typeKey,
      localizedPairs: { key: undefined, values: namePairs.filter((p) => p.value) },
      descriptionPairs: descriptionPairs.some((p) => p.value) ? { values: descriptionPairs.filter((p) => p.value) } : null,
      correspondingMedia: { mediaTypeId: 1, linkLocalizations: imagePairs.filter((p) => (p.mediaLink ?? "").trim()) },
      positions,
      startDateTime: isCalendar && startDateTime ? new Date(startDateTime).toISOString() : null,
      endDateTime: isCalendar && endDateTime ? new Date(endDateTime).toISOString() : null,
    });
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: { xs: '95vh', sm: '85vh' }, m: { xs: 0.5, sm: 2 } } }}
      >
        <DialogTitle sx={{ bgcolor: "primary.main", color: "white", pr: 6, py: 2.5 }}>
          <Typography variant="h6" fontWeight={700} component="span" sx={{ display: "block" }}>
            <LocalText>{title ?? (readOnly ? "View Custom Item" : isEditing ? "Edit Custom Item" : "New Custom Item")}</LocalText>
          </Typography>
          <Typography component="span" variant="caption" sx={{ color: "rgba(255,255,255,0.6)", mt: 0.25, display: "block" }}>
            <LocalText>{subtitle ?? "Name, description, and image shared · Link and thumbnail per position"}</LocalText>
          </Typography>
          <IconButton onClick={(e) => { e.currentTarget.blur(); onClose(); }} size="small" sx={{ position: "absolute", right: 10, top: 12, color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, overflow: "auto" }}>
          {typesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

              {/* Option Type */}
              <Box>
                <SectionLabel required>Option Type</SectionLabel>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={typeId}
                  onChange={(e) => !readOnly && setTypeId(e.target.value)}
                  disabled={readOnly}
                >
                  {types.map((t) => (
                    <MenuItem key={t.id} value={t.id}><LocalText>{t.name}</LocalText></MenuItem>
                  ))}
                </TextField>
              </Box>

              <Divider />

              {/* Shared: Name */}
              <Box>
                <SectionLabel required>Name</SectionLabel>
                <DropdownTextField label="Name" values={namePairs} attribute="value" onChange={setNamePairs} disabled={readOnly} required={!readOnly} />
              </Box>

              {/* Shared: Description */}
              <Box>
                <SectionLabel>Description</SectionLabel>
                <DropdownTextField label="Description" values={descriptionPairs} attribute="value" onChange={setDescriptionPairs} disabled={readOnly} />
              </Box>

              <Divider />

              {/* Position navigation */}
              <Box>
                <SectionLabel>Position</SectionLabel>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {POSITION_LABELS.map((label, index) => {
                    const isActive = activePosition === index;
                    const pv = positionValidation[index];
                    const pos = positions[index];
                    const hasInvalidUrl = !isActive && (pv?.linkOk === false || pv?.thumbnailOk === false);
                    const isPartial = !isActive && positionIsPartial(pos);
                    const isComplete = !isActive && positionIsComplete(pos);
                    const borderColor = isActive ? "primary.main"
                      : hasInvalidUrl ? "error.main"
                      : isPartial     ? "#ed6c02"
                      : "primary.lighter";

                    return (
                      <Button
                        key={label}
                        onClick={() => setActivePosition(index)}
                        size="small"
                        sx={{
                          flex: 1, borderRadius: 2, textTransform: "none",
                          fontWeight: isActive ? 700 : 500,
                          bgcolor: isActive ? "primary.main" : "transparent",
                          color: isActive ? "white" : "primary.main",
                          border: "2px solid", borderColor, py: 0.75, position: "relative",
                          "&:hover": {
                            bgcolor: isActive ? "primary.dark" : "rgba(10,50,109,0.06)",
                            borderColor: hasInvalidUrl ? "error.dark" : isPartial ? "#e65100" : "primary.main",
                          },
                        }}
                      >
                        {label}
                        {hasInvalidUrl && <Box sx={{ position: "absolute", top: 5, right: 6, width: 7, height: 7, borderRadius: "50%", bgcolor: "error.main" }} />}
                        {isPartial && !hasInvalidUrl && <Box sx={{ position: "absolute", top: 5, right: 6, width: 7, height: 7, borderRadius: "50%", bgcolor: "#ed6c02" }} />}
                        {isComplete && !hasInvalidUrl && !isPartial && <Box sx={{ position: "absolute", top: 5, right: 6, width: 7, height: 7, borderRadius: "50%", bgcolor: "success.main" }} />}
                      </Button>
                    );
                  })}
                </Box>
              </Box>

              {/* Per-position fields — re-mount on switch to reset DropdownTextField internal state */}
              <Box key={activePosition} sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <SectionLabel>Link</SectionLabel>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={linkPlaceholder}
                    value={position.link}
                    onChange={(e) => updatePosition("link", e.target.value)}
                    disabled={readOnly}
                    error={!readOnly && linkStatus === 'invalid'}
                    helperText={linkHelperText}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LinkIcon sx={{ fontSize: 16, color: "text.secondary" }} /></InputAdornment>,
                      endAdornment: !readOnly ? <InputAdornment position="end"><UrlStatusIcon status={linkStatus} /></InputAdornment> : undefined,
                    }}
                  />
                  <IconButton size="small" disabled={!position.link} onClick={(e) => { e.currentTarget.blur(); setLinkPreviewOpen(true); }} sx={{ color: "text.secondary", "&:hover": { color: "primary.main" } }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Box>

                <SectionLabel>Thumbnail</SectionLabel>
                <DropdownTextField
                  label="Image URL"
                  values={position.thumbnailLocalizations}
                  attribute="mediaLink"
                  onChange={(v) => updatePosition("thumbnailLocalizations", v)}
                  previewable={!readOnly}
                  mediaType="image"
                  urlValidationType={readOnly ? null : "image"}
                  onValidationChange={readOnly ? undefined : handleThumbnailValidation}
                  disabled={readOnly}
                  required={!readOnly && positionIsPartial(position)}
                />
              </Box>

              <Divider />

              {/* Shared: Main image */}
              <Box>
                <SectionLabel required>Image</SectionLabel>
                <DropdownTextField
                  label="Image URL"
                  values={imagePairs}
                  attribute="mediaLink"
                  onChange={setImagePairs}
                  previewable={!readOnly}
                  mediaType="image"
                  urlValidationType={readOnly ? null : "image"}
                  onValidationChange={readOnly ? undefined : handleImageValidation}
                  disabled={readOnly}
                  required={!readOnly}
                />
              </Box>

              {isCalendar && (
                <>
                  <Divider />
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <SectionLabel required>Start</SectionLabel>
                      <TextField fullWidth size="small" type="datetime-local" value={startDateTime} onChange={(e) => !readOnly && setStartDateTime(e.target.value)} disabled={readOnly} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <SectionLabel required>End</SectionLabel>
                      <TextField fullWidth size="small" type="datetime-local" value={endDateTime} onChange={(e) => !readOnly && setEndDateTime(e.target.value)} disabled={readOnly} />
                    </Box>
                  </Box>
                  {!calendarValid && (startDateTime || endDateTime) && (
                    <Typography variant="caption" sx={{ color: "#d32f2f" }}><LocalText>End must be after start.</LocalText></Typography>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", p: 3, bgcolor: "grey.50" }}>
          {readOnly ? (
            <Button onClick={onClose} size="large" sx={{ px: 8, py: 1.5, fontWeight: 700, borderRadius: 2, width: { xs: "100%", sm: "50%" } }}>
              <LocalText>Close</LocalText>
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSave} disabled={!isValid} size="large" sx={{ px: 8, py: 1.5, fontWeight: 700, borderRadius: 2, width: { xs: "100%", sm: "50%" } }}>
              <LocalText>{saveLabel ?? (isEditing ? "Save Changes" : "Save Custom Item")}</LocalText>
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <MediaPreviewOverlay open={linkPreviewOpen} onClose={() => setLinkPreviewOpen(false)} type="link" url={position.link} />
    </>
  );
};

export default CustomItemModal;
